<template>
  <form class="py-12" v-on:submit.prevent="prevent">
    <v-slide-x-transition>
      <v-card class="mx-auto my-3" max-width="600" :loading="loading">
        <v-card-title>
          {{ $t("auth.password.title") }}
        </v-card-title>
        <v-card-text>
          <div class="d-flex align-center">
            <v-avatar size="64">
              <v-img width="64" height="64" cover :src="user.logo" v-if="user && user.logo"></v-img>
              <img width="100" height="100" :src="logo" v-else />
            </v-avatar>
            <div class="pl-3">
              <div class="text-h6" v-if="user && user.name">
                {{ user.name }}
              </div>
              <div v-if="user.publisherNotFound"></div>
              <div class="text-caption" v-else>
                {{ $t("auth.password.subtitle") }}
              </div>
            </div>
          </div>
          <v-alert type="warning" icon="mdi-alert" dense outlined v-if="user.publisherNotFound">Você não possui
            <strong>conta de veículo</strong> associada ao seu
            e-mail. Digite sua senha para prosseguir no cadastro</v-alert>

          <v-text-field v-model="password" ref="password" :error-messages="passwordErrors"
            :label="$t('auth.password.password.label')" required :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'" @click:append="showPassword = !showPassword"
            @input="v$.password.$touch()" @blur="v$.password.$touch()" @keyup.enter="submit"></v-text-field>

          <turnstile-widget v-model="turnstileToken" class="mt-2"></turnstile-widget>
        </v-card-text>
        <v-card-actions class="d-flex justify-end">
          <div class="d-flex justify-space-between align-center" style="width: 100%">
            <v-switch v-model="keepConnected" :label="$t('auth.password.keep.label')" class="ml-2"></v-switch>
            <v-btn class="mr-2" color="primary" elevation="0" type="button" :loading="processing"
              :disabled="(v$.$invalid && v$.$dirty) || !turnstileToken" @click="submit()">
              {{ $t("auth.buttons.enter") }}
            </v-btn>
          </div>
        </v-card-actions>

        <v-divider></v-divider>
        <v-card-actions>
          <div class="d-flex">
            <v-btn text class="d-block" @click="$emit('setPage', 'user')">
              {{ $t("auth.buttons.different") }}
            </v-btn>
            <v-btn color="primary" class="ml-3 d-block" @click="$emit('setPage', 'recovery')">
              {{ $t("auth.buttons.recovery") }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-slide-x-transition>
  </form>
</template>
<script>
import useVuelidate from "@vuelidate/core";
import { required, minLength } from "@vuelidate/validators";
import { BASE_URL } from '@/http';
import moment from 'moment-timezone'
import { saveAuthAndRedirect } from '@/services/authService'
import TurnstileWidget from './TurnstileWidget.vue'

export default {
  components: {
    TurnstileWidget,
  },

  setup() {
    return { v$: useVuelidate() };
  },

  validations: {
    password: { required, minLength: minLength(6) },
  },

  props: {
    user: Object,
  },

  data() {
    return {
      showPassword: false,
      processing: false,
      loading: false,
      password: "",
      keepConnected: false,
      turnstileToken: "",
      logo: `${BASE_URL}/assets/image?w=100&h=100&txt=${this.user.name[this.user.name.length - 1]}&bg=909090&txtclr=ffffff&txtsize=44`,
    };
  },

  computed: {
    passwordErrors() {
      const errors = [];
      if (!this.v$.password.$dirty) return errors;
      this.v$.password.minLength.$invalid &&
        errors.push(this.$t("auth.password.password.errors.min", { count: 6 }));
      this.v$.password.required.$invalid &&
        errors.push(this.$t("auth.password.password.errors.required"));
      return errors;
    },
  },

  mounted() {
    this.$refs.password.focus();
  },

  methods: {
    prevent(event) {
      event.stopPropagation();
    },

    submit() {
      this.processing = true;
      this.v$.$touch();
      if (!this.user.email || this.v$.$invalid) {
        this.processing = false;
        return;
      }

      const form = {
        email:           this.user.email,
        password:        this.password,
        keepConnected:   this.keepConnected,
        profileTypeId:   2,
        registerCompany: this.user.publisherNotFound ? true : false,
        turnstileToken:  this.turnstileToken,
      };

      this.$axios.post('auth/do', form).then(
        (response) => {
          this.processing = false;
          const data = response.data.data;

          if (data.dataMissing) {
            const links = [`hash=${data.hash}`];
            if (data.redirect === 'materialUpload')  links.push('step=3');
            if (data.redirect === 'whitelabel')       links.push('step=4');
            if (data.redirect === 'company')          links.push('step=2');
            if (data.redirect === 'termSignature') {
              links.push(`term=${data.accession.termId}`);
              links.push(`doc=${data.accession.url}`);
              links.push('step=5');
            }
            if (data.redirect === 'isLegal' || data.redirect === 'confirmLegal') {
              links.push('step=5');
            }
            links.push(`loc=${this.user.location}`);
            this.$root.loading = true;
            this.$router.replace(`/cadastrar?${links.join('&')}`);
            return;
          }

          this.$root.loading = true;
          saveAuthAndRedirect(data, this.$route.query.r, { keepConnected: this.keepConnected });
        },
        (error) => {
          this.processing = false;
          const errs = error.response?.data?.errors;
          if (!errs?.length || !errs[0]) return;

          const first = errs[0];
          if (first.map) {
            const text = first.map.includes('blocked')
              ? this.$t(first.map, { param: moment(first.param, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YY HH:mm') })
              : this.$tc(first.map, first.param + 1, { param: first.param + 1 });
            this.$emit('error', { text, show: true });
            return;
          }

          const msg = Object.values(first)?.[0]?.[0] ?? '';
          const keyMap = {
            'Ops... e-mail e/ou senha inválidos.': 'auth.password.password.errors.invalid',
            'Para acessar a plataforma, precisamos que você valide seu e-mail. Enviamos um novo e-mail para você.': 'auth.password.password.errors.needToConfirm',
            'Ops... e-mail e/ou senha inválidos. Se errar novamente na próxima tentativa, a sua conta será bloqueada por 15 minutos.': 'auth.password.password.errors.lastChance',
            'Por motivos de segurança sua conta está bloqueada. Para mais informações ou dúvidas entre em contato conosco.': 'auth.password.password.errors.blockedAccount',
            'Não foi possível confirmar que você não é um robô. Tente novamente.': 'auth.password.password.errors.captcha',
          };
          if (keyMap[msg]) {
            this.$emit('error', { text: this.$t(keyMap[msg]), show: true });
          }
        }
      );
    },
  },
};
</script>
